import { useEffect, useRef, useState } from 'react';
import { Extension } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import { TextStyle, FontFamily, FontSize } from '@tiptap/extension-text-style';
import { marked } from 'marked';
import '../styles/RichTextEditor.css';

const FONT_FAMILIES = [
  { label: 'Predvolene', value: '' },
  { label: 'Inter', value: 'Inter, sans-serif' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", serif' },
  { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Courier', value: '"Courier New", monospace' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Lora', value: 'Lora, serif' },
];

const FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px', '32px'];
const LINE_HEIGHTS = ['0.1', '0.2', '0.3', '0.4', '0.5', '0.6', '0.7', '0.8', '0.9', '1', '1.2', '1.4', '1.6', '1.8', '2'];
const BLOCK_SPACINGS = ['0', '0.25rem', '0.5rem', '0.75rem', '1rem', '1.5rem', '2rem'];
const IMAGE_WIDTHS = ['20%', '25%', '33%', '40%', '50%', '60%', '75%', '100%'];
const FONT_WEIGHTS = [
  { label: 'Tenké', value: '300' },
  { label: 'Normálne', value: '400' },
  { label: 'Stredné', value: '500' },
  { label: 'Polotučné', value: '600' },
  { label: 'Tučné', value: '700' },
];
const IMAGE_LAYOUTS = [
  { label: 'Blok', value: 'block' },
  { label: 'Obtekanie vľavo', value: 'wrap-left' },
  { label: 'Obtekanie vpravo', value: 'wrap-right' },
];

const hasHtml = (value) => /<[a-z][\s\S]*>/i.test(value || '');

const toEditorContent = (value) => {
  if (!value) return '';
  if (hasHtml(value)) return value;
  return marked.parse(value, { breaks: true, gfm: true });
};

const normalizeHtml = (editor) => {
  const html = editor.getHTML();
  const text = editor.getText().trim();
  const hasMedia = /<(img|figure|iframe)\b/i.test(html);
  return text === '' && !hasMedia ? '' : html;
};

const FontWeight = Extension.create({
  name: 'fontWeight',

  addOptions() {
    return {
      types: ['textStyle'],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontWeight: {
            default: null,
            parseHTML: (element) => element.style.fontWeight || null,
            renderHTML: (attributes) => {
              if (!attributes.fontWeight) return {};
              return { style: `font-weight: ${attributes.fontWeight}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontWeight: (fontWeight) => ({ chain }) => chain().setMark('textStyle', { fontWeight }).run(),
      unsetFontWeight: () => ({ chain }) => chain().setMark('textStyle', { fontWeight: null }).removeEmptyTextStyle().run(),
    };
  },
});

const BlockSpacing = Extension.create({
  name: 'blockSpacing',

  addOptions() {
    return {
      types: ['paragraph', 'heading', 'bulletList', 'orderedList'],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          spacing: {
            default: null,
            parseHTML: (element) => element.style.marginBottom || null,
            renderHTML: (attributes) => {
              if (!attributes.spacing) return {};
              return { style: `margin-bottom: ${attributes.spacing}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setBlockSpacing: (spacing) => ({ editor }) => {
        const types = this.options.types;
        for (const type of types) {
          if (editor.isActive(type)) {
            return editor.chain().focus().updateAttributes(type, { spacing }).run();
          }
        }

        return editor.chain().focus().setParagraph().updateAttributes('paragraph', { spacing }).run();
      },
      unsetBlockSpacing: () => ({ editor }) => {
        const types = this.options.types;
        for (const type of types) {
          if (editor.isActive(type)) {
            return editor.chain().focus().updateAttributes(type, { spacing: null }).run();
          }
        }

        return editor.chain().focus().updateAttributes('paragraph', { spacing: null }).run();
      },
    };
  },
});

const LineHeight = Extension.create({
  name: 'lineHeight',

  addOptions() {
    return {
      types: ['paragraph', 'heading', 'bulletList', 'orderedList', 'blockquote'],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          lineHeight: {
            default: null,
            parseHTML: (element) => element.style.lineHeight || null,
            renderHTML: (attributes) => {
              if (!attributes.lineHeight) return {};
              return { style: `line-height: ${attributes.lineHeight}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setLineHeight: (lineHeight) => ({ editor }) => {
        const types = this.options.types;
        for (const type of types) {
          if (editor.isActive(type)) {
            return editor.chain().focus().updateAttributes(type, { lineHeight }).run();
          }
        }

        return editor.chain().focus().setParagraph().updateAttributes('paragraph', { lineHeight }).run();
      },
      unsetLineHeight: () => ({ editor }) => {
        const types = this.options.types;
        for (const type of types) {
          if (editor.isActive(type)) {
            return editor.chain().focus().updateAttributes(type, { lineHeight: null }).run();
          }
        }

        return editor.chain().focus().updateAttributes('paragraph', { lineHeight: null }).run();
      },
    };
  },
});

const StyledImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: '100%',
        parseHTML: (element) => element.getAttribute('data-width') || element.style.width || '100%',
        renderHTML: (attributes) => ({ 'data-width': attributes.width || '100%' }),
      },
      align: {
        default: 'center',
        parseHTML: (element) => element.getAttribute('data-align') || 'center',
        renderHTML: (attributes) => ({ 'data-align': attributes.align || 'center' }),
      },
      layout: {
        default: 'block',
        parseHTML: (element) => element.getAttribute('data-layout') || 'block',
        renderHTML: (attributes) => ({ 'data-layout': attributes.layout || 'block' }),
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    const align = HTMLAttributes.align || 'center';
    const width = HTMLAttributes.width || '100%';
    const layout = HTMLAttributes.layout || 'block';
    const styleParts = [HTMLAttributes.style, `width: ${width}`, 'height: auto'];

    if (layout === 'wrap-left') {
      styleParts.push('display: block', 'float: left', 'margin: 0.25rem 1rem 0.75rem 0');
    } else if (layout === 'wrap-right') {
      styleParts.push('display: block', 'float: right', 'margin: 0.25rem 0 0.75rem 1rem');
    } else {
      styleParts.push('display: block');
    }

    if (layout !== 'wrap-left' && layout !== 'wrap-right' && align === 'left') {
      styleParts.push('margin-left: 0', 'margin-right: auto');
    } else if (layout !== 'wrap-left' && layout !== 'wrap-right' && align === 'right') {
      styleParts.push('margin-left: auto', 'margin-right: 0');
    } else if (layout === 'block') {
      styleParts.push('margin-left: auto', 'margin-right: auto');
    }

    return ['img', { ...HTMLAttributes, style: styleParts.filter(Boolean).join('; ') }];
  },
});

function ToolbarButton({ active = false, label, onClick, disabled = false }) {
  return (
    <button
      type="button"
      className={`rich-text-editor__button${active ? ' is-active' : ''}`}
      onMouseDown={(event) => {
        event.preventDefault();
        if (!disabled) onClick();
      }}
      disabled={disabled}
    >
      {label}
    </button>
  );
}

function ToolbarSelect({ label, value, options, onChange, disabled = false }) {
  return (
    <label className="rich-text-editor__select-wrap">
      <span className="sr-only">{label}</span>
      <select
        className="rich-text-editor__select"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value || option} value={option.value || option}>
            {option.label || option}
          </option>
        ))}
      </select>
    </label>
  );
}

const getToolbarState = (editor) => {
  const textStyleAttributes = editor.getAttributes('textStyle');
  const paragraphAttributes = editor.getAttributes('paragraph');
  const headingAttributes = editor.getAttributes('heading');
  const bulletListAttributes = editor.getAttributes('bulletList');
  const orderedListAttributes = editor.getAttributes('orderedList');
  const blockquoteAttributes = editor.getAttributes('blockquote');
  const imageAttributes = editor.getAttributes('image');
  const hasSelectedImage = editor.isActive('image');

  return {
    fontFamily: textStyleAttributes.fontFamily || '',
    fontSize: textStyleAttributes.fontSize || '16px',
    fontWeight: textStyleAttributes.fontWeight || '400',
    lineHeight:
      paragraphAttributes.lineHeight ||
      headingAttributes.lineHeight ||
      bulletListAttributes.lineHeight ||
      orderedListAttributes.lineHeight ||
      blockquoteAttributes.lineHeight ||
      '1.4',
    spacing:
      paragraphAttributes.spacing ||
      headingAttributes.spacing ||
      bulletListAttributes.spacing ||
      orderedListAttributes.spacing ||
      '0.5rem',
    imageWidth: imageAttributes.width || '100%',
    imageAlign: imageAttributes.align || 'center',
    imageLayout: imageAttributes.layout || 'block',
    hasSelectedImage,
    isBold: editor.isActive('bold'),
    isItalic: editor.isActive('italic'),
    isUnderline: editor.isActive('underline'),
    isStrike: editor.isActive('strike'),
    isLink: editor.isActive('link'),
    isBulletList: editor.isActive('bulletList'),
    isOrderedList: editor.isActive('orderedList'),
    isAlignLeft: editor.isActive({ textAlign: 'left' }),
    isAlignCenter: editor.isActive({ textAlign: 'center' }),
    isAlignRight: editor.isActive({ textAlign: 'right' }),
    isAlignJustify: editor.isActive({ textAlign: 'justify' }),
    blockType: editor.isActive('heading', { level: 1 })
      ? 'h1'
      : editor.isActive('heading', { level: 2 })
      ? 'h2'
      : editor.isActive('heading', { level: 3 })
      ? 'h3'
      : 'paragraph',
  };
};

function RichTextEditor({ value, onChange, onUploadImage, onUploadStateChange, minHeight = 240 }) {
  const fileInputRef = useRef(null);
  const lastEmittedValueRef = useRef(value || '');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [toolbarState, setToolbarState] = useState(null);

  const initialContent = toEditorContent(value || '');

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      FontFamily,
      FontSize,
      LineHeight,
      FontWeight,
      BlockSpacing,
      StyledImage,
      Link.configure({ openOnClick: false, autolink: true, defaultProtocol: 'https' }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: initialContent,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'rich-text-editor__content',
        style: `min-height: ${minHeight}px`,
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      const nextValue = normalizeHtml(currentEditor);
      lastEmittedValueRef.current = nextValue;
      onChange(nextValue);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const nextValue = value || '';
    if (nextValue === lastEmittedValueRef.current) return;
    editor.commands.setContent(toEditorContent(nextValue), false);
  }, [editor, value]);

  useEffect(() => {
    if (!onUploadStateChange) return;
    onUploadStateChange(uploadingImage);
  }, [onUploadStateChange, uploadingImage]);

  useEffect(() => {
    if (!editor) return;

    const syncToolbarState = () => {
      setToolbarState(getToolbarState(editor));
    };

    syncToolbarState();
    editor.on('selectionUpdate', syncToolbarState);
    editor.on('transaction', syncToolbarState);
    editor.on('focus', syncToolbarState);

    return () => {
      editor.off('selectionUpdate', syncToolbarState);
      editor.off('transaction', syncToolbarState);
      editor.off('focus', syncToolbarState);
    };
  }, [editor]);

  const updateSelectedImage = (nextAttributes) => {
    if (!editor) return false;

    const { state, view } = editor;
    const { selection, tr } = state;

    if (selection.node?.type?.name === 'image') {
      const position = selection.from;
      const attrs = {
        ...selection.node.attrs,
        ...nextAttributes,
      };

      view.dispatch(tr.setNodeMarkup(position, undefined, attrs));
      editor.commands.focus(position);
      return true;
    }

    return editor.chain().focus().updateAttributes('image', nextAttributes).run();
  };

  const insertLink = () => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href || '';
    const url = window.prompt('Zadajte URL odkazu', previousUrl);

    if (url === null) return;

    if (!url.trim()) {
      editor.chain().focus().unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };

  const handleImagePick = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !editor || !onUploadImage) return;

    setUploadingImage(true);

    try {
      const url = await onUploadImage(file);
      editor.chain().focus().setImage({ src: url, alt: file.name, width: '50%', align: 'center', layout: 'block' }).run();
    } catch (error) {
      alert(error.message || 'Nahratie obrázka zlyhalo');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  if (!editor) return null;

  const currentToolbarState = toolbarState || getToolbarState(editor);
  const currentFontFamily = currentToolbarState.fontFamily;
  const currentFontSize = currentToolbarState.fontSize;
  const currentFontWeight = currentToolbarState.fontWeight;
  const currentLineHeight = currentToolbarState.lineHeight;
  const currentSpacing = currentToolbarState.spacing;
  const currentImageWidth = currentToolbarState.imageWidth;
  const currentImageAlign = currentToolbarState.imageAlign;
  const currentImageLayout = currentToolbarState.imageLayout;
  const hasSelectedImage = currentToolbarState.hasSelectedImage;

  const nudgeImageWidth = (step) => {
    const current = parseInt(currentImageWidth, 10);
    const next = Number.isNaN(current) ? 50 : Math.max(10, Math.min(100, current + step));
    updateSelectedImage({ width: `${next}%` });
  };

  const blockType = currentToolbarState.blockType;

  return (
    <div className="rich-text-editor">
      <div className="rich-text-editor__toolbar">
        <div className="rich-text-editor__group">
          <div className="rich-text-editor__group-label">Text</div>
          <div className="rich-text-editor__group-controls">
            <ToolbarSelect
              label="Písmo"
              value={currentFontFamily}
              options={FONT_FAMILIES}
              onChange={(nextValue) => {
                if (!nextValue) editor.chain().focus().unsetFontFamily().run();
                else editor.chain().focus().setFontFamily(nextValue).run();
              }}
            />

            <ToolbarSelect
              label="Veľkosť písma"
              value={currentFontSize}
              options={FONT_SIZES}
              onChange={(nextValue) => editor.chain().focus().setFontSize(nextValue).run()}
            />

            <ToolbarSelect
              label="Hrúbka písma"
              value={currentFontWeight}
              options={FONT_WEIGHTS}
              onChange={(nextValue) => editor.chain().focus().setFontWeight(nextValue).run()}
            />

            <ToolbarButton label="Tučné" active={currentToolbarState.isBold} onClick={() => editor.chain().focus().toggleBold().run()} />
            <ToolbarButton label="Kurzíva" active={currentToolbarState.isItalic} onClick={() => editor.chain().focus().toggleItalic().run()} />
            <ToolbarButton label="Podčiarknutie" active={currentToolbarState.isUnderline} onClick={() => editor.chain().focus().toggleUnderline().run()} />
            <ToolbarButton label="Prečiarknutie" active={currentToolbarState.isStrike} onClick={() => editor.chain().focus().toggleStrike().run()} />
            <ToolbarButton label="Odkaz" active={currentToolbarState.isLink} onClick={insertLink} />
          </div>
        </div>

        <div className="rich-text-editor__group">
          <div className="rich-text-editor__group-label">Odsek</div>
          <div className="rich-text-editor__group-controls">
            <ToolbarSelect
              label="Štýl bloku"
              value={blockType}
              options={[
                { label: 'Odsek', value: 'paragraph' },
                { label: 'Nadpis 1', value: 'h1' },
                { label: 'Nadpis 2', value: 'h2' },
                { label: 'Nadpis 3', value: 'h3' },
              ]}
              onChange={(nextValue) => {
                if (nextValue === 'paragraph') editor.chain().focus().setParagraph().run();
                else editor.chain().focus().toggleHeading({ level: Number(nextValue.slice(1)) }).run();
              }}
            />

            <ToolbarSelect
              label="Riadkovanie"
              value={currentLineHeight}
              options={LINE_HEIGHTS}
              onChange={(nextValue) => editor.chain().focus().setLineHeight(nextValue).run()}
            />

            <ToolbarSelect
              label="Rozostupy"
              value={currentSpacing}
              options={BLOCK_SPACINGS}
              onChange={(nextValue) => editor.chain().focus().setBlockSpacing(nextValue).run()}
            />

            <ToolbarButton label="Odrážky" active={currentToolbarState.isBulletList} onClick={() => editor.chain().focus().toggleBulletList().run()} />
            <ToolbarButton label="Číslovaný zoznam" active={currentToolbarState.isOrderedList} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
            <ToolbarButton label="Zarovnať vľavo" active={currentToolbarState.isAlignLeft} onClick={() => editor.chain().focus().setTextAlign('left').run()} />
            <ToolbarButton label="Zarovnať na stred" active={currentToolbarState.isAlignCenter} onClick={() => editor.chain().focus().setTextAlign('center').run()} />
            <ToolbarButton label="Zarovnať vpravo" active={currentToolbarState.isAlignRight} onClick={() => editor.chain().focus().setTextAlign('right').run()} />
            <ToolbarButton label="Do bloku" active={currentToolbarState.isAlignJustify} onClick={() => editor.chain().focus().setTextAlign('justify').run()} />
            <ToolbarButton label="Vymazať formátovanie" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} />
          </div>
        </div>
      </div>

      <div className="rich-text-editor__toolbar rich-text-editor__toolbar--secondary">
        <div className="rich-text-editor__group">
          <div className="rich-text-editor__group-label">Obrázok</div>
          <div className="rich-text-editor__group-controls">
            <ToolbarButton label={uploadingImage ? 'Nahráva sa obrázok…' : 'Vložiť obrázok'} onClick={() => fileInputRef.current?.click()} disabled={uploadingImage} />

            <ToolbarSelect
              label="Rozloženie obrázka"
              value={currentImageLayout}
              options={IMAGE_LAYOUTS}
              disabled={!hasSelectedImage}
              onChange={(nextValue) => updateSelectedImage({ layout: nextValue })}
            />

            <ToolbarSelect
              label="Šírka obrázka"
              value={currentImageWidth}
              options={IMAGE_WIDTHS}
              disabled={!hasSelectedImage}
              onChange={(nextValue) => updateSelectedImage({ width: nextValue })}
            />

            <ToolbarButton label="Zmenšiť obrázok" disabled={!hasSelectedImage} onClick={() => nudgeImageWidth(-5)} />
            <ToolbarButton label="Zväčšiť obrázok" disabled={!hasSelectedImage} onClick={() => nudgeImageWidth(5)} />
            <ToolbarButton label="Obrázok vľavo" active={hasSelectedImage && currentImageAlign === 'left'} disabled={!hasSelectedImage} onClick={() => updateSelectedImage({ align: 'left' })} />
            <ToolbarButton label="Obrázok na stred" active={hasSelectedImage && currentImageAlign === 'center'} disabled={!hasSelectedImage} onClick={() => updateSelectedImage({ align: 'center' })} />
            <ToolbarButton label="Obrázok vpravo" active={hasSelectedImage && currentImageAlign === 'right'} disabled={!hasSelectedImage} onClick={() => updateSelectedImage({ align: 'right' })} />
          </div>
        </div>
      </div>

      <EditorContent editor={editor} />
      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleImagePick} />
    </div>
  );
}

export default RichTextEditor;
